import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, ArrowRight, Scale, Car, Building2, MapPin, BookOpen, FileText, MessageCircle, Calculator, Shield, DollarSign, Camera, AlertCircle, Home, Receipt, Settings, GraduationCap, AlertTriangle, Radio, Book, Send, Clipboard } from 'lucide-react';
import Fuse from 'fuse.js';
import { allSchools, schoolRankings2024 } from '../data/education-data';

// Icon mapping for dynamic icon rendering
const ICONS = {
  Scale, Car, Building2, MapPin, BookOpen, FileText, MessageCircle, Calculator,
  Shield, DollarSign, Camera, AlertCircle, Home, Receipt, Settings, GraduationCap,
  AlertTriangle, Radio, Book, Send, Clipboard, Search
};

const GlobalSearch = ({
  setPage,
  howToGuides = [],
  serviceProviders = { tutors: [], test_prep: [], supplies: [], uniforms: [] },
  lang = 'en'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Define all searchable pages
  const pages = [
    { id: 'home', title: 'Home', description: 'Main page with all features', icon: 'Home', category: 'Pages' },
    { id: 'all-schools', title: 'Find Schools', description: 'Search all 1,654 schools — infant through university', icon: 'Building2', category: 'Pages', keywords: ['school', 'find', 'search', 'directory', 'all schools', 'primary', 'infant', 'prep', 'college', 'university'] },
    { id: 'schools', title: 'High Schools', description: 'Secondary schools, CSEC rankings, exams and sixth form', icon: 'Building2', category: 'Pages', keywords: ['high school', 'secondary', 'compare', 'grade 7', 'sixth form'] },
    { id: 'rankings', title: 'School Rankings', description: 'Compare schools by exam performance', icon: 'Scale', category: 'Pages', keywords: ['ranking', 'best', 'top', 'performance', 'results'] },
    { id: 'exams', title: 'Exams Hub', description: 'Information about PEP, CSEC, CAPE exams', icon: 'FileText', category: 'Pages', keywords: ['exam', 'csec', 'cape', 'pep', 'test'] },
    { id: 'exams-pep', title: 'PEP Exam Guide', description: 'Primary Exit Profile exam information', icon: 'GraduationCap', category: 'Pages', keywords: ['pep', 'primary', 'grade 6', 'placement'] },
    { id: 'exams-csec', title: 'CSEC Exam Guide', description: 'Caribbean Secondary Education Certificate', icon: 'FileText', category: 'Pages', keywords: ['csec', 'cxc', 'secondary', 'subjects'] },
    { id: 'exams-cape', title: 'CAPE Exam Guide', description: 'Caribbean Advanced Proficiency Examination', icon: 'GraduationCap', category: 'Pages', keywords: ['cape', 'sixth form', 'advanced', 'university'] },
    { id: 'how-school-works', title: 'How School Works', description: 'Understanding Jamaica education system', icon: 'BookOpen', category: 'Pages', keywords: ['education', 'system', 'levels', 'pathway'] },
    { id: 'enrollment', title: 'Enrollment Guide', description: 'How to enroll your child in school', icon: 'Clipboard', category: 'Pages', keywords: ['enroll', 'register', 'admission', 'apply'] },
    { id: 'funding', title: 'Scholarships', description: 'Find scholarships and financial aid', icon: 'DollarSign', category: 'Pages', keywords: ['scholarship', 'funding', 'grant', 'bursary', 'financial aid'] },
    { id: 'education-jobs', title: 'Education Jobs', description: 'Teaching and education careers', icon: 'Building2', category: 'Pages', keywords: ['job', 'teaching', 'career', 'vacancy'] },
    { id: 'services', title: 'Education Services', description: 'Find tutors, supplies, uniforms', icon: 'Building2', category: 'Pages', keywords: ['tutor', 'tutoring', 'supplies', 'uniform', 'service'] },
    { id: 'getlisted', title: 'Get Listed', description: 'Add your education service to directory', icon: 'Send', category: 'Pages', keywords: ['list', 'business', 'advertise', 'directory'] },
    { id: 'help', title: 'Help & FAQs', description: 'Frequently asked questions', icon: 'MessageCircle', category: 'Pages', keywords: ['help', 'faq', 'question', 'support'] },
    { id: 'settings', title: 'Settings', description: 'App settings and preferences', icon: 'Settings', category: 'Pages', keywords: ['setting', 'preference', 'config'] },
    { id: 'contact', title: 'Contact Us', description: 'Get in touch with us', icon: 'Send', category: 'Pages', keywords: ['contact', 'email', 'message', 'support'] },
    { id: 'privacy', title: 'Privacy Policy', description: 'Our privacy policy', icon: 'FileText', category: 'Pages', keywords: ['privacy', 'policy', 'data'] },
  ];

  // Build search index
  const buildSearchIndex = useCallback(() => {
    const index = [];

    // Add pages
    pages.forEach(page => {
      index.push({
        type: 'page',
        id: page.id,
        title: page.title,
        description: page.description,
        icon: page.icon,
        category: page.category || 'Pages',
        keywords: [page.title.toLowerCase(), page.description.toLowerCase(), ...(page.keywords || [])].join(' '),
        action: page.external
          ? () => window.open(page.external, '_blank', 'noopener,noreferrer')
          : () => setPage(page.id)
      });
    });

    // Note: Offences search index removed - this is now an education platform

    // Add How-To Guides
    howToGuides.forEach(guide => {
      index.push({
        type: 'guide',
        id: guide.id,
        title: guide.title,
        description: guide.subtitle || guide.category,
        icon: 'Clipboard',
        category: 'How-To Guides',
        keywords: [
          guide.title.toLowerCase(),
          (guide.subtitle || '').toLowerCase(),
          (guide.category || '').toLowerCase(),
          'guide', 'how to', 'steps'
        ].join(' '),
        action: () => setPage('howto'),
        data: guide
      });
    });

    // Add Service Providers
    const addServiceProviders = (providers, type, iconName) => {
      (providers || []).forEach(provider => {
        index.push({
          type: 'service',
          id: `${type}-${provider.id}`,
          title: provider.name,
          description: `${provider.parish} | ${provider.services || type}`,
          icon: iconName,
          category: 'Service Providers',
          keywords: [
            provider.name.toLowerCase(),
            (provider.parish || '').toLowerCase(),
            (provider.services || '').toLowerCase(),
            (provider.specialty || '').toLowerCase(),
            type.toLowerCase(),
            'service', 'provider'
          ].join(' '),
          action: () => setPage('services'),
          data: { type, ...provider }
        });
      });
    };

    // Add Education Service Providers (tutors, test prep, etc.)
    addServiceProviders(serviceProviders.tutors, 'Tutor', 'GraduationCap');
    addServiceProviders(serviceProviders.test_prep, 'Exam Prep', 'FileText');
    addServiceProviders(serviceProviders.supplies, 'School Supplies', 'Book');
    addServiceProviders(serviceProviders.uniforms, 'Uniform Supplier', 'Building2');

    // Add Schools (secondary school directory) — enriched with ranking where available
    const rankingByName = new Map();
    (schoolRankings2024 || []).forEach(r => {
      if (r && r.school_name) rankingByName.set(r.school_name.toLowerCase().trim(), r);
    });

    const seenSchools = new Set();
    (allSchools || []).forEach(school => {
      const name = school && school.school_name;
      if (!name) return;
      const key = name.toLowerCase().trim();
      if (seenSchools.has(key)) return;
      seenSchools.add(key);

      const ranking = rankingByName.get(key);
      const descParts = [school.parish, school.sector, school.school_type].filter(Boolean);
      if (ranking) descParts.unshift(`Ranked #${ranking.rank} (${ranking.year})`);

      index.push({
        type: 'school',
        id: `school-${key}`,
        title: name,
        description: descParts.join(' • '),
        icon: ranking ? 'Scale' : 'Building2',
        category: 'Schools',
        keywords: [
          name.toLowerCase(),
          (school.parish || '').toLowerCase(),
          (school.sector || '').toLowerCase(),
          (school.school_type || '').toLowerCase(),
          ranking ? 'ranking rank top best performance' : '',
          'school high secondary'
        ].join(' '),
        // Ranked schools open the rankings page; others open the directory
        action: () => setPage(ranking ? 'rankings' : 'schools')
      });
    });

    // Add any ranked schools that aren't in the directory list (name variations)
    (schoolRankings2024 || []).forEach(r => {
      if (!r || !r.school_name) return;
      const key = r.school_name.toLowerCase().trim();
      if (seenSchools.has(key)) return;
      seenSchools.add(key);
      index.push({
        type: 'ranking',
        id: `ranking-${key}`,
        title: r.school_name,
        description: [`Ranked #${r.rank} (${r.year})`, r.parish, r.gender].filter(Boolean).join(' • '),
        icon: 'Scale',
        category: 'Schools',
        keywords: [
          r.school_name.toLowerCase(),
          (r.parish || '').toLowerCase(),
          (r.gender || '').toLowerCase(),
          'ranking rank top best performance school'
        ].join(' '),
        action: () => setPage('rankings')
      });
    });

    return index;
  }, [howToGuides, serviceProviders, setPage]);

  // Memoized search index
  const searchIndex = useMemo(() => buildSearchIndex(), [buildSearchIndex]);

  // Fuse.js instance for fuzzy search
  const fuse = useMemo(() => new Fuse(searchIndex, {
    keys: [
      { name: 'title', weight: 0.4 },
      { name: 'keywords', weight: 0.3 },
      { name: 'description', weight: 0.2 },
      { name: 'category', weight: 0.1 }
    ],
    threshold: 0.4, // Allow fuzzy matching (0 = exact, 1 = match anything)
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2
  }), [searchIndex]);

  // Fuzzy search function with improved relevance
  const search = useCallback((searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const q = searchQuery.toLowerCase().trim();

    // Use Fuse.js for fuzzy search
    const fuseResults = fuse.search(q);

    // Map results and add relevance info
    const matches = fuseResults.map(result => ({
      ...result.item,
      score: result.score
    }));

    // Sort: exact title matches first, then pages, then by score
    matches.sort((a, b) => {
      const aTitle = (a.title || '').toLowerCase();
      const bTitle = (b.title || '').toLowerCase();
      const aExact = aTitle.includes(q) ? 1 : 0;
      const bExact = bTitle.includes(q) ? 1 : 0;

      // Exact title matches first
      if (aExact !== bExact) return bExact - aExact;

      // Pages prioritized over other types
      if (a.type === 'page' && b.type !== 'page') return -1;
      if (b.type === 'page' && a.type !== 'page') return 1;

      // Then by score (lower is better)
      return (a.score || 0) - (b.score || 0);
    });

    setResults(matches.slice(0, 15)); // Show more results with fuzzy search
    setSelectedIndex(0);
  }, [fuse]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => search(query), 150);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open search with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }

      // Close with Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  // Handle result selection
  const handleSelect = (result) => {
    result.action();
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = ICONS[iconName] || Search;
    return IconComponent;
  };

  // Category colors
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Pages': return 'bg-brand-100 text-brand-700';
      case 'Schools': return 'bg-blue-100 text-blue-700';
      case 'How-To Guides': return 'bg-amber-100 text-amber-700';
      case 'Service Providers': return 'bg-purple-100 text-purple-700';
      case 'Education': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Search Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white min-h-[44px]"
        aria-label="Open search (Ctrl+K)"
        aria-haspopup="dialog"
      >
        <Search className="w-4 h-4" aria-hidden="true" />
        <span className="hidden md:inline text-sm">Search</span>
        <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-white/20 rounded" aria-hidden="true">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[15vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-dialog-title"
        >
          <div className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Search className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <label htmlFor="global-search-input" className="sr-only" id="search-dialog-title">Search the site</label>
              <input
                id="global-search-input"
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search schools, exams, guides, services..."
                className="flex-1 text-lg outline-none bg-transparent text-gray-900 placeholder:text-gray-400"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-controls="search-results"
                aria-expanded={results.length > 0}
                aria-activedescendant={results.length > 0 ? `search-result-${selectedIndex}` : undefined}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4 text-gray-400" aria-hidden="true" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded hover:bg-gray-200 min-h-[44px]"
                aria-label="Close search"
              >
                ESC
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto" id="search-results" role="listbox" aria-label="Search results">
              {results.length > 0 ? (
                <div className="py-2">
                  {results.map((result, index) => {
                    const IconComponent = getIcon(result.icon);
                    return (
                      <button
                        key={result.id}
                        id={`search-result-${index}`}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors min-h-[56px] ${
                          index === selectedIndex ? 'bg-brand-50' : 'hover:bg-gray-50'
                        }`}
                        role="option"
                        aria-selected={index === selectedIndex}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          index === selectedIndex ? 'bg-brand-100' : 'bg-gray-100'
                        }`} aria-hidden="true">
                          <IconComponent className={`w-5 h-5 ${
                            index === selectedIndex ? 'text-brand-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{result.title}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${getCategoryColor(result.category)}`}>
                              {result.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">{result.description}</p>
                        </div>
                        <ArrowRight className={`w-4 h-4 flex-shrink-0 ${
                          index === selectedIndex ? 'text-brand-500' : 'text-gray-300'
                        }`} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              ) : query ? (
                <div className="py-12 text-center text-gray-500" role="status" aria-live="polite">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" aria-hidden="true" />
                  <p className="font-medium">No results found</p>
                  <p className="text-sm">Try searching for schools, exams, or services</p>
                </div>
              ) : (
                <div className="py-6 px-4">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3" id="quick-links-label">Quick Links</p>
                  <div className="grid grid-cols-2 gap-2" role="group" aria-labelledby="quick-links-label">
                    {[
                      { id: 'all-schools', title: 'Find Schools', icon: Building2 },
                      { id: 'rankings', title: 'Rankings', icon: Scale },
                      { id: 'exams', title: 'Exam Guides', icon: FileText },
                      { id: 'services', title: 'Tutors & Services', icon: GraduationCap },
                      { id: 'funding', title: 'Scholarships', icon: DollarSign },
                      { id: 'help', title: 'Help & FAQs', icon: MessageCircle },
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setPage(item.id); setIsOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700 transition-colors text-sm min-h-[44px]"
                      >
                        <item.icon className="w-4 h-4" aria-hidden="true" />
                        <span>{item.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-500" aria-hidden="true">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↓</kbd>
                  to navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↵</kbd>
                  to select
                </span>
              </div>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">esc</kbd>
                to close
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
